import { useDriverManagement } from '@/api/useDriverManagement'
import type { components } from '@/types/api/v1'
import { CalendarOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Card, DatePicker, Popover, Select, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { type ReactNode, useMemo, useState } from 'react'
// import { calculatePatternScheduleStartDate, dateToOffsetDay } from '../TemplateManagement/utils'

type Row = components['schemas']['DriverInfoRow']
export type ScheduleTemplateValue = Pick<
  Row,
  'preferred_template' | 'preferred_template_days' | 'preferred_template_id' | 'pattern_schedule_start_date'
>

export type ScheduleTemplateEditorProps = {
  driver: Row
  onChange: (driver: Row, value: ScheduleTemplateValue) => void
  children?: ReactNode
}

export function ScheduleTemplateEditor({ driver, onChange, children }: ScheduleTemplateEditorProps) {
  const [value, setValue] = useState(driver?.preferred_template)
  const [selectedDate, setSelectedDate] = useState(
    driver?.pattern_schedule_start_date ? dayjs(driver.pattern_schedule_start_date) : undefined
  )
  const [visible, setVisible] = useState(false)
  const { useDriverTemplates } = useDriverManagement()
  const { data: templates, isLoading } = useDriverTemplates()

  const options = useMemo(
    () => [
      { value: null, label: 'No Template' },
      ...(templates?.map((x) => ({ value: x.name, label: x.name }))?.sort((a, b) => a.label.localeCompare(b.label)) ??
        []),
    ],
    [templates]
  )
  const requiresDateSelection = useMemo(() => {
    const daysInSchedule = templates?.find((x) => x.name === value)?.days?.length
    return daysInSchedule !== undefined && daysInSchedule !== 7
  }, [templates, value])

  const isInvalid = requiresDateSelection && selectedDate === undefined

  function handleTemplateChange(newValue: string | null) {
    setValue(newValue)
    if (newValue === null) {
      setSelectedDate(undefined)
    }
  }

  function handleSubmit() {
    if (isInvalid) return
    const selectedOption = templates?.find((x) => x.name === value)
    const result: ScheduleTemplateValue = {}
    result.preferred_template_id = selectedOption?.id ?? null
    result.preferred_template = selectedOption?.name ?? null
    result.preferred_template_days = selectedOption?.days ?? null
    result.pattern_schedule_start_date =
      selectedDate === undefined || !requiresDateSelection
        ? null
        : dayjs.tz(selectedDate, 'UTC').startOf('day').toISOString()
    onChange(driver, result)
    setVisible(false)
  }

  return (
    <Popover
      open={visible}
      trigger='click'
      destroyTooltipOnHide
      placement='bottomRight'
      onOpenChange={setVisible}
      content={
        <Card
          style={{ width: '400px' }}
          styles={{ body: { display: 'flex', flexDirection: 'column', gap: '8px' } }}
          title='Select Schedule Template'
          actions={[
            <Horizontal key='1' gap={8} justifyContent='flex-end' className='px-4'>
              <GraviButton
                buttonText='Cancel'
                onClick={() => {
                  setVisible(false)
                  setValue('')
                  setSelectedDate(undefined)
                }}
              />
              <GraviButton
                data-cy='save-template'
                disabled={isInvalid}
                buttonText='Save'
                theme2
                onClick={handleSubmit}
              />
            </Horizontal>,
          ]}
        >
          <Select
            data-cy='template-input'
            allowClear
            showSearch
            loading={isLoading}
            options={options}
            autoFocus
            defaultOpen
            value={value}
            onChange={handleTemplateChange}
          />
          {requiresDateSelection && (
            <DatePicker
              data-cy='pattern-start-date-input'
              format='MM-DD-YYYY'
              value={selectedDate}
              onChange={setSelectedDate}
            />
          )}
        </Card>
      }
    >
      {children ?? 'Configure Template'}
    </Popover>
  )
}

export const scheduleTemplateColumn: ColDef<Row, string> = {
  headerName: 'Schedule Template',
  colId: 'preferred_template',
  width: 200,
  minWidth: 200,
  cellEditor: ScheduleTemplateEditor,
  valueGetter: ({ data }) => data?.preferred_template,
  cellRenderer: ({ value }: ICellRendererParams<Row, string>) => {
    if (!value) return 'No template assigned'

    return (
      <Horizontal gap={8} alignItems='center'>
        <Tooltip title='Schedule Template'>
          <CalendarOutlined style={{ color: 'var(--theme-color-2)' }} />
        </Tooltip>
        <Texto>{value}</Texto>
      </Horizontal>
    )
  },
}
